import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Sparkles, CreditCard, AlertTriangle, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageTitle } from "./DashboardLayout";
import { Button } from "@/components/ui/button";

const money = (c) => `$${(c / 100).toLocaleString()}`;

export default function Subscription() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [interval, setInterval] = useState("monthly");
  const [busy, setBusy] = useState("");
  const { data } = useQuery({ queryKey: ["subscription"], queryFn: () => api.get("/subscription").then((r) => r.data) });

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    api.get(`/subscription/status/${sid}`).then((r) => {
      if (r.data.status === "complete") { toast.success("Subscription active — welcome aboard!"); qc.invalidateQueries({ queryKey: ["subscription"] }); }
    }).finally(() => { params.delete("session_id"); setParams(params, { replace: true }); });
  }, []); // eslint-disable-line

  const subscribe = async (plan) => {
    setBusy(plan);
    try {
      const r = await api.post("/subscription/checkout", { plan, interval, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(""); }
  };
  const portal = async () => {
    try { const r = await api.post("/subscription/portal", { origin_url: window.location.origin }); window.location.href = r.data.url; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return null;
  const plans = data.plans || {};
  const status = data.billingStatus;
  const isPrimary = data.plan === "platform";

  return (
    <div>
      <PageTitle title="Subscription" subtitle="Manage your TDW Nexus AI workspace plan" action={
        data.hasStripeCustomer ? <Button variant="outline" className="gap-1.5" onClick={portal} data-testid="manage-billing-btn"><CreditCard className="h-4 w-4" /> Manage billing</Button> : null
      } />

      {isPrimary ? (
        <div className="rounded-xl bg-navy text-white p-6 mb-6" data-testid="sub-status"><p className="font-heading text-lg font-semibold">Primary workspace</p><p className="text-sm text-slate-300 mt-1">Your workspace is comped and always active.</p></div>
      ) : status === "active" ? (
        <div className="rounded-xl bg-green-50 border border-green-200 p-5 mb-6 flex items-center gap-3" data-testid="sub-status"><Check className="h-5 w-5 text-green-600" /><div><p className="font-medium text-navy capitalize">{data.plan} plan active</p>{data.currentPeriodEnd && <p className="text-xs text-slate-500">Renews {new Date(data.currentPeriodEnd).toLocaleDateString()}</p>}</div></div>
      ) : status === "trial" ? (
        <div className="rounded-xl bg-blue-50 border border-blue-200 p-5 mb-6 flex items-center gap-3" data-testid="sub-status"><Clock className="h-5 w-5 text-blue-600" /><div><p className="font-medium text-navy">Free trial — {data.trialDaysLeft} day{data.trialDaysLeft === 1 ? "" : "s"} left</p><p className="text-xs text-slate-500">Choose a plan below to keep your workspace active after the trial.</p></div></div>
      ) : (
        <div className="rounded-xl bg-amber-50 border border-amber-300 p-5 mb-6 flex items-center gap-3" data-testid="sub-status"><AlertTriangle className="h-5 w-5 text-amber-600" /><div><p className="font-medium text-navy">{status === "past_due" ? "Payment past due" : "Subscription canceled"}</p><p className="text-xs text-slate-600">Your staff are locked out until billing is resolved. Choose a plan or update your card to reactivate.</p></div></div>
      )}

      {!isPrimary && (
        <>
          <div className="flex justify-center mb-6">
            <div className="inline-flex rounded-full bg-slate-100 p-1" data-testid="interval-toggle">
              {["monthly", "annual"].map((iv) => (
                <button key={iv} onClick={() => setInterval(iv)} data-testid={`interval-${iv}`}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize transition-colors ${interval === iv ? "bg-white text-navy shadow-sm" : "text-slate-500"}`}>
                  {iv}{iv === "annual" && <span className="ml-1 text-xs text-electric">save ~17%</span>}
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {Object.entries(plans).map(([key, p]) => {
              const active = data.plan === key && status === "active";
              return (
                <div key={key} className={`rounded-xl border p-6 flex flex-col ${key === "pro" ? "border-electric shadow-lg" : "border-slate-200"}`} data-testid={`plan-card-${key}`}>
                  {key === "pro" && <span className="self-start text-[10px] font-mono uppercase tracking-wider text-white bg-electric px-2 py-0.5 rounded-full mb-2 inline-flex items-center gap-1"><Sparkles className="h-3 w-3" /> Popular</span>}
                  <h3 className="font-heading text-xl font-bold text-navy">{p.name}</h3>
                  <p className="mt-2"><span className="font-heading text-3xl font-bold text-navy">{money(p[interval])}</span><span className="text-slate-500 text-sm">/{interval === "monthly" ? "mo" : "yr"}</span></p>
                  <ul className="mt-4 space-y-2 flex-1">
                    {p.features.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><Check className="h-4 w-4 text-electric mt-0.5 shrink-0" /> {f}</li>)}
                  </ul>
                  <Button disabled={active || busy === key} onClick={() => subscribe(key)} className={`mt-5 w-full ${key === "pro" ? "bg-electric hover:bg-tech" : "bg-navy hover:bg-navy/90"}`} data-testid={`subscribe-${key}`}>
                    {active ? "Current plan" : busy === key ? "Redirecting…" : "Subscribe"}
                  </Button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
